/**
 * BRING YOUR PROJECT.
 *
 * A GC arriving at OMNΛS already has a project — in Primavera, in Excel, in a
 * procurement log somebody maintains by hand. Until now the only way in was
 * to retype it. This is the door.
 *
 * The flow is deliberately four steps, never one:
 *
 *   CHOOSE WHAT   →   SELECT FILE   →   PREVIEW + VALIDATION   →   CONFIRM
 *
 * There is no one-click import, and there is no "import anyway". An import
 * writes into the same register that decides whether work can start; a file
 * nobody looked at would put unreviewed obligations behind that decision.
 * The preview exists so a human sees what will land, and the confirmation is
 * bound to the exact bytes they saw.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSequencer } from "../../lib/sequencer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Separator } from "../../components/ui/separator";
import {
  Upload,
  FileSpreadsheet,
  CalendarRange,
  Truck,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import projectImportApi from "../../services/projectImportService";
import { classifyWorkflowError } from "../../lib/workflowErrors";
import { recordBetaSignal } from "../../lib/betaTelemetry";
import ImportPreviewTable from "./ImportPreviewTable";
import {
  IMPORT_KINDS,
  IMPORT_STATES,
  importKindDisplay,
  importColumns,
  importStateDisplay,
  summarizePreview,
  previewState,
  canCommit,
  groupIssues,
  issueVisibility,
  presentImportError,
  presentImportResult,
  describeChosenFile,
  IMPORTED_PROVENANCE,
} from "./importPresentation";

const KIND_ICON = {
  schedule: CalendarRange,
  procurement: Truck,
  requirements: ClipboardList,
};

const TONE_CLASS = {
  good: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  attention: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  bad: "bg-red-500/20 text-red-400 border-red-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

/** Step 1 — which of the three, and what each one actually loads. */
const KindPicker = ({ onChoose }) => (
  <div className="space-y-2">
    <p className="text-sm text-muted-foreground">
      Load what you already have. Each import adds records — it never changes or replaces one that
      is already here.
    </p>
    <div className="grid gap-2 sm:grid-cols-3">
      {IMPORT_KINDS.map((kind) => {
        const display = importKindDisplay(kind);
        const Icon = KIND_ICON[kind];
        return (
          <button
            key={kind}
            type="button"
            data-testid={`import-kind-${kind}`}
            onClick={() => onChoose(kind)}
            className="text-left rounded-lg border border-border/60 bg-muted/20 p-3 hover:border-primary/60 hover:bg-muted/40 transition-colors"
          >
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Icon className="w-4 h-4 text-primary" />
              {display.label}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{display.describes}</p>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Up to {display.rowCap.toLocaleString()} rows · .csv or .xlsx
            </p>
          </button>
        );
      })}
    </div>
  </div>
);

/** The columns the file may carry, so an operator can prepare one. */
const ColumnHelp = ({ kind }) => {
  const columns = importColumns(kind);
  return (
    <details className="rounded-lg border border-border/60 bg-muted/10 p-3">
      <summary className="text-xs font-medium cursor-pointer select-none">
        Columns this import reads ({columns.length})
      </summary>
      <div className="mt-2 space-y-1">
        {columns.map((col) => (
          <div key={col.column} className="flex flex-wrap items-baseline gap-x-2 text-[11px]">
            <span className="font-mono text-foreground">{col.column}</span>
            {col.required && (
              <span className="text-[9px] uppercase tracking-wide text-amber-400">required</span>
            )}
            <span className="text-muted-foreground">{col.describes}</span>
          </div>
        ))}
        <p className="text-[10px] text-muted-foreground pt-1.5 border-t border-border/40 mt-2">
          Column headings go in the first row, in lower case with underscores. Anything else in the
          file is ignored.
        </p>
      </div>
    </details>
  );
};

const BringYourProjectDialog = ({ open, onOpenChange, projectId, onImported }) => {
  const [kind, setKind] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [refusal, setRefusal] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  /**
   * A preview is only ever confirmable against the file and kind it was taken
   * for. Two guards enforce that together, because either alone leaves a gap:
   *
   *   the sequencer discards a response whose request has been superseded —
   *   close/reopen, choose-another-file, or choose-another-kind can all put a
   *   second preview in flight while the first is still running, and without
   *   this the SLOWER one wins;
   *
   *   the subject tag makes the survivor prove which file and kind it
   *   describes, so a response that slips through can still never be
   *   rendered beside the wrong Import button.
   *
   * The failure both exist to prevent is the worst this dialog has: a
   * preview on screen for file A, with file B's bytes and A's `confirm_hash`
   * going to the server.
   */
  const previewSeq = useRef(createSequencer()).current;
  const commitSeq = useRef(createSequencer()).current;

  const reset = useCallback(() => {
    // Anything in flight is retired first: a response that lands after a
    // reset must not repopulate the dialog it was reset out of.
    previewSeq.invalidate();
    commitSeq.invalidate();
    setKind(null);
    setFile(null);
    setPreview(null);
    setPreviewing(false);
    setCommitting(false);
    setRefusal(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [previewSeq, commitSeq]);

  // Reopening starts clean: a previous file's preview must never be
  // confirmable against a new one.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  /**
   * A payload is this dialog's answer only if it describes the file and kind
   * currently on screen. Anything else reads as "not previewed" — never as
   * this file's verdict.
   */
  const forThisSubject = (tagged) =>
    tagged && tagged.file === file && tagged.kind === kind && tagged.projectId === projectId;

  const previewData = forThisSubject(preview) ? preview.data : null;
  const activeRefusal = forThisSubject(refusal) ? refusal.presented : null;

  const summary = useMemo(() => summarizePreview(previewData), [previewData]);
  const state = useMemo(
    () => (activeRefusal ? activeRefusal.state : previewState(summary)),
    [activeRefusal, summary]
  );
  const chosen = useMemo(() => describeChosenFile(file, kind), [file, kind]);
  const issueGroups = useMemo(() => groupIssues(previewData), [previewData]);
  const issuesVisible = useMemo(() => issueVisibility(previewData), [previewData]);

  const runPreview = async (chosenFile) => {
    if (!projectId || !kind || !chosenFile) return;
    const ticket = previewSeq.next();
    // The subject this request is for, captured now and checked on arrival.
    const subject = { projectId, kind, file: chosenFile };
    setPreviewing(true);
    setRefusal(null);
    setPreview(null);
    setResult(null);
    try {
      const data = await projectImportApi.preview(projectId, kind, chosenFile);
      if (!previewSeq.isCurrent(ticket)) return;
      setPreview({ ...subject, data });
    } catch (err) {
      if (!previewSeq.isCurrent(ticket)) return;
      const classified = classifyWorkflowError(err);
      const presented = presentImportError(classified, kind);
      setRefusal({ ...subject, presented });
      recordBetaSignal("validation-error", {
        feature: `import:${kind}:preview`,
        code: classified.code || null,
      });
    } finally {
      if (previewSeq.isCurrent(ticket)) setPreviewing(false);
    }
  };

  const chooseFile = (event) => {
    const chosenFile = event.target.files?.[0] || null;
    setFile(chosenFile);
    setPreview(null);
    setRefusal(null);
    setResult(null);
    /**
     * Clearing the input's value is what makes re-choosing the SAME path fire
     * a change event again. Without it, an operator who fixes the spreadsheet
     * and picks the same file gets no new preview — and the stale summary on
     * screen still carries the OLD sha, which is what the Import button would
     * send. They would be confirming bytes that no longer exist.
     */
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (chosenFile) runPreview(chosenFile);
  };

  /**
   * The confirmation.
   *
   * `fileSha256` is the sha the preview computed over these exact bytes, and
   * it is sent back as `confirm_hash`. If the file changed underneath — a
   * re-save, an edit, a different copy — the server refuses with 409 and this
   * NEVER retries: a stale confirmation is precisely what the hash exists to
   * catch, and re-sending it would defeat the check.
   */
  const commit = async () => {
    // `committing` is checked here as well as on the button: a double click
    // can fire two handlers before React re-renders the disabled state, and
    // an import is not idempotent.
    if (!projectId || !kind || !file || !summary?.fileSha256 || committing) return;
    const ticket = commitSeq.next();
    const subject = { projectId, kind, file };
    setCommitting(true);
    setRefusal(null);
    try {
      const data = await projectImportApi.commit(projectId, kind, file, summary.fileSha256);
      /**
       * A write that already landed is NOT discarded when its subject moved —
       * the records exist and pretending otherwise would be a lie. But it is
       * not rendered into a dialog that has moved on either: the toast still
       * reports it, and the reload still fires.
       */
      const presented = presentImportResult(data, kind);
      toast.success(`${importKindDisplay(kind).label} imported`, {
        description: presented.headline,
      });
      if (onImported) onImported(kind, presented);
      if (!commitSeq.isCurrent(ticket)) return;
      setResult({ ...subject, presented });
      setPreview(null);
    } catch (err) {
      if (!commitSeq.isCurrent(ticket)) return;
      const classified = classifyWorkflowError(err);
      const presented = presentImportError(classified, kind);
      setRefusal({ ...subject, presented });
      recordBetaSignal("validation-error", {
        feature: `import:${kind}:commit`,
        code: classified.code || null,
      });
      // A changed file invalidates the preview it was confirmed against, so
      // the preview is dropped rather than left offering a stale Import.
      if (presented.state === IMPORT_STATES.PREVIEW_CHANGED) setPreview(null);
    } finally {
      if (commitSeq.isCurrent(ticket)) setCommitting(false);
    }
  };

  const activeResult = forThisSubject(result) ? result.presented : null;
  const display = kind ? importKindDisplay(kind) : null;
  const stateDisplay = state ? importStateDisplay(state) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-y-auto"
        data-testid="bring-your-project-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-primary" />
            Bring your project
          </DialogTitle>
          <DialogDescription>
            {display
              ? `${display.label} — ${display.describes}`
              : "Load a schedule, a procurement log, or a requirements list you already keep."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!kind && <KindPicker onChoose={setKind} />}

          {kind && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => {
                    // Retire anything in flight: a preview for the kind being
                    // left must not land under the kind being chosen.
                    previewSeq.invalidate();
                    commitSeq.invalidate();
                    setKind(null);
                    setFile(null);
                    setPreview(null);
                    setPreviewing(false);
                    setRefusal(null);
                    setResult(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                  Choose something else
                </Button>
                {stateDisplay && (
                  <Badge
                    variant="outline"
                    className={`${TONE_CLASS[stateDisplay.tone]} text-[10px]`}
                    data-testid="import-state"
                  >
                    {stateDisplay.label}
                  </Badge>
                )}
              </div>

              {!activeResult && <ColumnHelp kind={kind} />}

              {/* ---- file selection ---- */}
              {!activeResult && (
                <div className="rounded-lg border border-dashed border-border p-4 text-center space-y-2">
                  <FileSpreadsheet className="w-6 h-6 text-muted-foreground mx-auto" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={chooseFile}
                    className="hidden"
                    id="bring-your-project-file"
                    data-testid="import-file-input"
                  />
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={previewing || committing}
                      data-testid="import-choose-file"
                    >
                      {file ? "Choose a different file" : "Choose a file"}
                    </Button>
                  </div>
                  {chosen && (
                    <div className="text-[11px] text-muted-foreground space-y-0.5">
                      <p className="font-medium text-foreground break-all">{chosen.name}</p>
                      <p>
                        {(chosen.size / 1024).toFixed(0)} KB · read as {chosen.format} · up to{" "}
                        {chosen.rowCap.toLocaleString()} rows
                      </p>
                      {chosen.notes.map((note) => (
                        <p key={note} className="text-amber-400">
                          {note}
                        </p>
                      ))}
                    </div>
                  )}
                  {!file && (
                    <p className="text-[11px] text-muted-foreground">
                      Nothing is imported until you have seen what it contains.
                    </p>
                  )}
                </div>
              )}

              {previewing && (
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Reading the file. Nothing has been written.
                </p>
              )}

              {/* ---- refusal ---- */}
              {activeRefusal && (
                <Alert
                  className={
                    activeRefusal.state === IMPORT_STATES.PREVIEW_CHANGED
                      ? "border-amber-500/40 bg-amber-500/10"
                      : "border-red-500/40 bg-red-500/10"
                  }
                  data-testid="import-refusal"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="space-y-1.5">
                    <p className="font-medium text-sm">{activeRefusal.title}</p>
                    <p className="text-xs">{activeRefusal.explanation}</p>
                    {activeRefusal.detail && (
                      <p className="text-[11px] text-muted-foreground">{activeRefusal.detail}</p>
                    )}
                    {activeRefusal.action === "preview-again" && file && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] mt-1"
                        onClick={() => runPreview(file)}
                        data-testid="import-preview-again"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                        Preview it again
                      </Button>
                    )}
                    {activeRefusal.rows?.length > 0 && (
                      <div className="pt-1">
                        <ImportPreviewTable
                          rows={activeRefusal.rows}
                          kind={kind}
                          onlyIssues
                          truncated={activeRefusal.rowsTruncated}
                        />
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* ---- preview ---- */}
              {summary && !activeResult && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span>
                      <span className="font-semibold">{summary.totalRows.toLocaleString()}</span>{" "}
                      <span className="text-muted-foreground">rows read</span>
                    </span>
                    <span>
                      <span className="font-semibold text-emerald-400">
                        {summary.validRows.toLocaleString()}
                      </span>{" "}
                      <span className="text-muted-foreground">usable</span>
                    </span>
                    {summary.rowsWithIssues > 0 && (
                      <span>
                        <span className="font-semibold text-red-400">
                          {summary.rowsWithIssues.toLocaleString()}
                        </span>{" "}
                        <span className="text-muted-foreground">with blocking issues</span>
                      </span>
                    )}
                    <span className="text-muted-foreground">
                      Columns: {summary.columnsFound.join(", ") || "none recognised"}
                    </span>
                  </div>

                  {summary.isEmpty && (
                    <Alert className="border-amber-500/40 bg-amber-500/10">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        This file has column headings but no rows beneath them. There is nothing to
                        import.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Blocking issues, grouped: one wrong column is one problem. */}
                  {summary.rowsWithIssues > 0 && (
                    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2">
                      <p className="text-xs font-medium text-red-400">
                        Nothing will be imported until these are fixed
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        An import is all-or-nothing, so one unusable row holds the whole file. Fix
                        them in your spreadsheet and choose the file again.
                      </p>
                      {/*
                        The row detail stops at 200 while the count covers the
                        file. When the broken rows are past that, the list is
                        EMPTY beside a non-zero count — so the gap is stated
                        rather than left looking like a screen with nothing on
                        it.
                      */}
                      {issuesVisible.noneVisible ? (
                        <p className="text-[11px] text-amber-400">
                          {summary.rowsWithIssues.toLocaleString()}{" "}
                          {summary.rowsWithIssues === 1 ? "row has" : "rows have"} blocking issues,
                          and all of them are past the first{" "}
                          {summary.shownRows.toLocaleString()} rows OMNΛS can show. Split the file
                          and preview the later part to see them.
                        </p>
                      ) : (
                        !issuesVisible.complete && (
                          <p className="text-[11px] text-amber-400">
                            Showing {issuesVisible.shown.toLocaleString()} of{" "}
                            {issuesVisible.total.toLocaleString()} rows with issues — the rest are
                            past the first {summary.shownRows.toLocaleString()} rows.
                          </p>
                        )
                      )}
                      {issueGroups.map((group) => (
                        <div key={group.issue} className="text-[11px]">
                          <p className="text-foreground">{group.issue}</p>
                          <p className="text-muted-foreground">
                            {group.count === 1 ? "Row" : `${group.count} rows —`}{" "}
                            {group.rows.filter((r) => r !== null).join(", ")}
                            {group.count > group.rows.length ? " and more" : ""}
                            {/* "About", because both readers skip blank rows —
                                any blank line above this one shifts the real
                                spreadsheet line down, and an exact promise
                                would send someone to the wrong row. */}
                            {group.rows[0] !== null
                              ? ` · about spreadsheet line ${group.rows[0] + 1}`
                              : ""}
                            {group.rows.length > 1 ? " onward" : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {kind === "requirements" && (
                    <Alert className="border-border bg-muted/20">
                      <Info className="h-4 w-4" />
                      <AlertDescription className="text-[11px] space-y-1">
                        <p className="font-medium">{IMPORTED_PROVENANCE.meaning}</p>
                        <p className="text-muted-foreground">{IMPORTED_PROVENANCE.consequence}</p>
                      </AlertDescription>
                    </Alert>
                  )}

                  <ImportPreviewTable
                    rows={summary.rows}
                    kind={kind}
                    truncated={summary.rowsTruncated}
                    totalRows={summary.totalRows}
                  />
                </div>
              )}

              {/* ---- result ---- */}
              {activeResult && (
                <div className="space-y-2" data-testid="import-result">
                  <Alert className="border-emerald-500/40 bg-emerald-500/10">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription className="space-y-1">
                      <p className="text-sm font-medium">{activeResult.headline}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Nothing that was already here was changed
                        {activeResult.updated === 0 ? "" : ` (${activeResult.updated} updated)`}.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {/* The register's own honesty payload. A note is not a failure. */}
                  {activeResult.warnings.length > 0 && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1">
                      <p className="text-xs font-medium text-amber-400">
                        Imported, with things OMNΛS could not establish
                      </p>
                      {activeResult.warnings.map((warning, index) => (
                        <p key={index} className="text-[11px] text-muted-foreground">
                          {warning}
                        </p>
                      ))}
                    </div>
                  )}
                  {activeResult.unresolvedConstraints.length > 0 && (
                    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-1">
                      <p className="text-xs font-medium">Constraints left unresolved</p>
                      {activeResult.unresolvedConstraints.map((constraint, index) => (
                        <p key={index} className="text-[11px] text-muted-foreground">
                          {typeof constraint === "string" ? constraint : JSON.stringify(constraint)}
                        </p>
                      ))}
                    </div>
                  )}
                  {activeResult.provenanceNote && (
                    <p className="text-[11px] text-muted-foreground">
                      {activeResult.provenanceNote.meaning} {activeResult.provenanceNote.consequence}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={reset}>
                      Import something else
                    </Button>
                    <Button size="sm" onClick={() => onOpenChange(false)}>
                      Done
                    </Button>
                  </div>
                </div>
              )}

              {/* ---- confirm ---- */}
              {summary && !activeResult && (
                <>
                  <Separator />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] text-muted-foreground max-w-md">
                      {canCommit(summary)
                        ? `Importing adds ${summary.totalRows.toLocaleString()} ${
                            summary.totalRows === 1 ? "record" : "records"
                          } to this project. It cannot be undone from here.`
                        : "This file cannot be imported as it stands."}
                    </p>
                    <Button
                      size="sm"
                      onClick={commit}
                      disabled={!canCommit(summary) || committing}
                      data-testid="import-commit"
                    >
                      {committing ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Import {summary.totalRows.toLocaleString()}{" "}
                      {summary.totalRows === 1 ? "row" : "rows"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BringYourProjectDialog;
